<template>
  <div class="account-management">
    <div class="page-header">
      <h2>后台账号管理</h2>
      <p>只管理平台后台和门店管理员账号，店员与顾客已拆分到独立页面</p>
    </div>
    
    <div class="page-content">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-box">
          <select v-model="roleFilter" class="search-select" @change="handleSearch">
            <option value="STORE_ADMIN">门店管理员</option>
            <option value="SUPER_ADMIN">超级管理员</option>
          </select>
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="请输入用户名/姓名搜索"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch" class="search-btn">搜索</button>
          <button @click="handleReset" class="reset-btn">重置</button>
          <button @click="handleAdd" class="add-btn">新增后台账号</button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-header">
          <h3>后台账号列表</h3>
        </div>
        
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>用户名</th>
                <th>手机号</th>
                <th>角色</th>
                <th>所属门店</th>
                <th>状态</th>
                <th>最后登录时间</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="8" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!userList || userList.length === 0" class="empty-row">
                <td colspan="8" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="user in userList" :key="user.id" class="data-row">
                <td>{{ user.username }}</td>
                <td>{{ user.phone || '-' }}</td>
                <td>{{ getRoleName(user.role) }}</td>
                <td>{{ getUserStoreNames(user) }}</td>
                <td>
                  <span :class="['status-badge', user.status ? 'active' : 'inactive']">
                    {{ user.status ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>{{ user.lastLoginTime ? formatDate(user.lastLoginTime) : '-' }}</td>
                <td>{{ formatDate(user.createdTime) }}</td>
                <td>
                  <button @click="handleEdit(user)" class="action-btn edit-btn">编辑</button>
                  <button @click="handleDelete(user)" class="action-btn delete-btn">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页区域 -->
        <div class="pagination-section" v-if="pagination.totalPages > 1">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button 
              @click="handlePageChange(0)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              首页
            </button>
            <button 
              @click="handlePageChange(pagination.number - 1)" 
              :disabled="pagination.first"
              class="page-btn"
            >
              上一页
            </button>
            <span class="page-numbers">
              <button 
                v-for="page in visiblePages" 
                :key="page"
                @click="handlePageChange(page - 1)"
                :class="['page-number', { active: page === pagination.number + 1 }]"
                class="page-btn"
              >
                {{ page }}
              </button>
            </span>
            <button 
              @click="handlePageChange(pagination.number + 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              下一页
            </button>
            <button 
              @click="handlePageChange(pagination.totalPages - 1)" 
              :disabled="pagination.last"
              class="page-btn"
            >
              末页
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增用户弹窗 -->
    <div v-if="showAddModal" class="modal-overlay" @click="handleCloseAddModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>新增后台账号</h3>
          <button @click="handleCloseAddModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleAddSubmit" class="user-form">
            <div class="form-group">
              <label for="username" class="required">用户名</label>
              <input
                id="username"
                v-model="addFormData.username"
                type="text"
                class="form-input"
                :class="{ 'error': addFormErrors.username }"
                placeholder="请输入用户名"
                required
              />
              <span v-if="addFormErrors.username" class="error-message">{{ addFormErrors.username }}</span>
            </div>

            <div class="form-group">
              <label for="password" class="required">密码</label>
              <input
                id="password"
                v-model="addFormData.password"
                type="password"
                class="form-input"
                :class="{ 'error': addFormErrors.password }"
                placeholder="请输入密码"
                required
              />
              <span v-if="addFormErrors.password" class="error-message">{{ addFormErrors.password }}</span>
            </div>

            <div class="form-group">
              <label for="phone" class="required">手机号</label>
              <input
                id="phone"
                v-model="addFormData.phone"
                type="tel"
                class="form-input"
                :class="{ 'error': addFormErrors.phone }"
                placeholder="请输入手机号"
                required
              />
              <span v-if="addFormErrors.phone" class="error-message">{{ addFormErrors.phone }}</span>
            </div>

            <div class="form-group">
              <label for="role" class="required">角色</label>
              <select
                id="role"
                v-model="addFormData.role"
                class="form-select"
                :class="{ 'error': addFormErrors.role }"
                @change="handleRoleChange('add')"
                required
              >
                <option value="">请选择角色</option>
                <option value="SUPER_ADMIN">超级管理员</option>
                <option value="STORE_ADMIN">门店管理员</option>
              </select>
              <span v-if="addFormErrors.role" class="error-message">{{ addFormErrors.role }}</span>
            </div>

            <div v-if="false" class="form-group">
              <label class="required">员工权限</label>
              <div class="permission-grid" :class="{ 'error-box': addFormErrors.employeePermissions }">
                <label
                  v-for="permission in permissionOptions"
                  :key="permission.value"
                  class="permission-item"
                >
                  <input
                    v-model="addFormData.employeePermissions"
                    type="checkbox"
                    :value="permission.value"
                  />
                  <span>{{ permission.label }}</span>
                </label>
              </div>
              <span v-if="addFormErrors.employeePermissions" class="error-message">{{ addFormErrors.employeePermissions }}</span>
            </div>

            <div v-if="addFormData.role === 'STORE_ADMIN'" class="form-group">
              <label class="required">可管理门店</label>
              <div class="permission-grid" :class="{ 'error-box': addFormErrors.managedStoreIds }">
                <label
                  v-for="store in storeList"
                  :key="store.id"
                  class="permission-item"
                >
                  <input
                    v-model="addFormData.managedStoreIds"
                    type="checkbox"
                    :value="store.id"
                  />
                  <span>{{ store.storeName }}</span>
                </label>
              </div>
              <span v-if="addFormErrors.managedStoreIds" class="error-message">{{ addFormErrors.managedStoreIds }}</span>
            </div>

            <div v-if="false" class="form-group">
              <label for="storeId">所属门店</label>
              <select
                id="storeId"
                v-model="addFormData.storeId"
                class="form-select"
                :class="{ 'error': addFormErrors.storeId }"
              >
                <option value="">请选择门店（可选）</option>
                <option v-for="store in storeList" :key="store.id" :value="store.id">
                  {{ store.storeName }}
                </option>
              </select>
              <span v-if="addFormErrors.storeId" class="error-message">{{ addFormErrors.storeId }}</span>
            </div>

            <div class="form-group">
              <label for="email">邮箱</label>
              <input
                id="email"
                v-model="addFormData.email"
                type="email"
                class="form-input"
                :class="{ 'error': addFormErrors.email }"
                placeholder="请输入邮箱（可选）"
              />
              <span v-if="addFormErrors.email" class="error-message">{{ addFormErrors.email }}</span>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="handleCloseAddModal" class="cancel-btn">取消</button>
          <button @click="handleAddSubmit" class="submit-btn" :disabled="addFormLoading">
            {{ addFormLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑用户弹窗 -->
    <div v-if="showEditModal" class="modal-overlay" @click="handleCloseEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑用户</h3>
          <button @click="handleCloseEditModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleEditSubmit" class="user-form">
            <div class="form-group">
              <label for="editUsername" class="required">用户名</label>
              <input
                id="editUsername"
                v-model="editFormData.username"
                type="text"
                class="form-input"
                :class="{ 'error': editFormErrors.username }"
                placeholder="请输入用户名"
                required
              />
              <span v-if="editFormErrors.username" class="error-message">{{ editFormErrors.username }}</span>
            </div>

            <div class="form-group">
              <label for="editPassword">密码</label>
              <input
                id="editPassword"
                v-model="editFormData.password"
                type="password"
                class="form-input"
                :class="{ 'error': editFormErrors.password }"
                placeholder="留空则不修改密码"
              />
              <span v-if="editFormErrors.password" class="error-message">{{ editFormErrors.password }}</span>
            </div>

            <div class="form-group">
              <label for="editPhone" class="required">手机号</label>
              <input
                id="editPhone"
                v-model="editFormData.phone"
                type="tel"
                class="form-input"
                :class="{ 'error': editFormErrors.phone }"
                placeholder="请输入手机号"
                required
              />
              <span v-if="editFormErrors.phone" class="error-message">{{ editFormErrors.phone }}</span>
            </div>

            <div v-if="editFormData.role === 'SUPER_ADMIN'" class="form-group">
              <label class="required">角色</label>
              <div class="readonly-role">超级管理员（系统保留账号）</div>
            </div>

            <div v-else class="form-group">
              <label for="editRole" class="required">角色</label>
              <select
                id="editRole"
                v-model="editFormData.role"
                class="form-select"
                :class="{ 'error': editFormErrors.role }"
                @change="handleRoleChange('edit')"
                required
              >
                <option value="">请选择角色</option>
                <option value="STORE_ADMIN">门店管理员</option>
              </select>
              <span v-if="editFormErrors.role" class="error-message">{{ editFormErrors.role }}</span>
            </div>

            <div v-if="false" class="form-group">
              <label class="required">员工权限</label>
              <div class="permission-grid" :class="{ 'error-box': editFormErrors.employeePermissions }">
                <label
                  v-for="permission in permissionOptions"
                  :key="permission.value"
                  class="permission-item"
                >
                  <input
                    v-model="editFormData.employeePermissions"
                    type="checkbox"
                    :value="permission.value"
                  />
                  <span>{{ permission.label }}</span>
                </label>
              </div>
              <span v-if="editFormErrors.employeePermissions" class="error-message">{{ editFormErrors.employeePermissions }}</span>
            </div>

            <div v-if="editFormData.role === 'STORE_ADMIN'" class="form-group">
              <label class="required">可管理门店</label>
              <div class="permission-grid" :class="{ 'error-box': editFormErrors.managedStoreIds }">
                <label
                  v-for="store in storeList"
                  :key="store.id"
                  class="permission-item"
                >
                  <input
                    v-model="editFormData.managedStoreIds"
                    type="checkbox"
                    :value="store.id"
                  />
                  <span>{{ store.storeName }}</span>
                </label>
              </div>
              <span v-if="editFormErrors.managedStoreIds" class="error-message">{{ editFormErrors.managedStoreIds }}</span>
            </div>

            <div v-if="false" class="form-group">
              <label for="editStoreId">所属门店</label>
              <select
                id="editStoreId"
                v-model="editFormData.storeId"
                class="form-select"
                :class="{ 'error': editFormErrors.storeId }"
              >
                <option value="">请选择门店（可选）</option>
                <option v-for="store in storeList" :key="store.id" :value="store.id">
                  {{ store.storeName }}
                </option>
              </select>
              <span v-if="editFormErrors.storeId" class="error-message">{{ editFormErrors.storeId }}</span>
            </div>

            <div class="form-group">
              <label for="editEmail">邮箱</label>
              <input
                id="editEmail"
                v-model="editFormData.email"
                type="email"
                class="form-input"
                :class="{ 'error': editFormErrors.email }"
                placeholder="请输入邮箱（可选）"
              />
              <span v-if="editFormErrors.email" class="error-message">{{ editFormErrors.email }}</span>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="handleCloseEditModal" class="cancel-btn">取消</button>
          <button @click="handleEditSubmit" class="submit-btn" :disabled="editFormLoading">
            {{ editFormLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="handleCloseDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>确认删除</h3>
          <button @click="handleCloseDeleteModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <p>确定要删除用户 "{{ deleteUserData.username }}" 吗？</p>
          <p class="warning-text">此操作不可撤销！</p>
        </div>
        <div class="modal-footer">
          <button @click="handleCloseDeleteModal" class="cancel-btn">取消</button>
          <button @click="handleDeleteConfirm" class="delete-confirm-btn" :disabled="deleteLoading">
            {{ deleteLoading ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'AccountManagement',
  data() {
    return {
      userList: [],
      storeList: [],
      loading: false,
      searchKeyword: '',
      roleFilter: 'STORE_ADMIN',
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      },
      showAddModal: false,
      addFormLoading: false,
      addFormData: {
        username: '',
        password: '',
        phone: '',
        role: 'STORE_ADMIN',
        storeId: '',
        email: '',
        employeePermissions: [],
        managedStoreIds: []
      },
      addFormErrors: {},
      showEditModal: false,
      editFormLoading: false,
      editFormData: {
        id: null,
        username: '',
        password: '',
        phone: '',
        role: 'STORE_ADMIN',
        storeId: '',
        email: '',
        employeePermissions: [],
        managedStoreIds: []
      },
      editFormErrors: {},
      showDeleteModal: false,
      deleteLoading: false,
      deleteUserData: {
        id: null,
        username: ''
      },
      permissionOptions: [
        { value: 'DEVICE_START', label: '设备上机' },
        { value: 'DEVICE_END', label: '设备下机' },
        { value: 'DEVICE_EXTEND', label: '设备延时' },
        { value: 'DEVICE_SWITCH', label: '设备换机' },
        { value: 'DEVICE_CLIENT_RESTART', label: '重启客户端' },
        { value: 'DEVICE_POWER_CONTROL', label: '插座通断电' },
        { value: 'ORDER_REFUND', label: '订单退款' },
        { value: 'ORDER_REVOKE', label: '订单撤销' },
        { value: 'MEMBER_VIEW', label: '查看会员' },
        { value: 'MEMBER_CREATE', label: '创建会员' },
        { value: 'MEMBER_EDIT', label: '编辑会员' },
        { value: 'MEMBER_RECHARGE', label: '会员充值' },
        { value: 'FREE_START', label: '免费上机' },
        { value: 'STATISTICS_VIEW', label: '查看统计' },
        { value: 'STORE_SETTING', label: '门店设置' }
      ]
    }
  },
  computed: {
    visiblePages() {
      const current = this.pagination.number + 1
      const total = this.pagination.totalPages
      const pages = []
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }
      
      return pages
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadUserList()
  },
  methods: {
    async loadStoreList() {
      try {
        const response = await request.get('/api/stores/getStores', {
          params: {
            page: 0,
            size: 1000
          }
        })
        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data.content || []
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
      }
    },

    async loadUserList() {
      this.loading = true
      try {
        const response = await request.get('/api/users/getUsers', {
          params: {
            page: this.pagination.number,
            size: this.pagination.size,
            keyword: this.searchKeyword,
            role: this.roleFilter
          }
        })
        
        if (response.data.success && response.data.code === 200) {
          this.userList = response.data.data.content || []
          this.pagination = {
            number: response.data.data.number || 0,
            size: response.data.data.size || 20,
            totalElements: response.data.data.totalElements || 0,
            totalPages: response.data.data.totalPages || 0,
            first: response.data.data.first || true,
            last: response.data.data.last || true
          }
        } else {
          throw new Error(response.data.message || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.userList = []
        this.pagination = {
          number: 0,
          size: 20,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true
        }
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.number = 0
      this.loadUserList()
    },

    handleReset() {
      this.searchKeyword = ''
      this.pagination.number = 0
      this.loadUserList()
    },

    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadUserList()
      }
    },

    getRoleName(role) {
      const roleMap = {
        'SUPER_ADMIN': '超级管理员',
        'STORE_ADMIN': '门店管理员'
      }
      return roleMap[role] || role
    },

    getStoreName(storeId) {
      if (!storeId) return '-'
      const store = this.storeList.find(s => s.id === storeId)
      return store ? store.storeName : '-'
    },

    getUserStoreNames(user) {
      if (!user) return '-'
      const managedIds = this.normalizeStoreIds(user.managedStoreIds)
      if (user.role === 'STORE_ADMIN' && managedIds.length > 0) {
        return managedIds.map(id => this.getStoreName(id)).filter(Boolean).join('、') || '-'
      }
      return this.getStoreName(user.storeId)
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    // 新增用户相关方法
    handleAdd() {
      this.showAddModal = true
      this.resetAddForm()
    },

    handleCloseAddModal() {
      this.showAddModal = false
      this.resetAddForm()
    },

    resetAddForm() {
      this.addFormData = {
        username: '',
        password: '',
        phone: '',
        role: 'STORE_ADMIN',
        storeId: '',
        email: '',
        employeePermissions: [],
        managedStoreIds: []
      }
      this.addFormErrors = {}
      this.addFormLoading = false
    },

    validateAddForm() {
      this.addFormErrors = {}
      let isValid = true

      if (!this.addFormData.username.trim()) {
        this.addFormErrors.username = '请输入用户名'
        isValid = false
      }

      if (!this.addFormData.password.trim()) {
        this.addFormErrors.password = '请输入密码'
        isValid = false
      }

      if (!this.addFormData.phone.trim()) {
        this.addFormErrors.phone = '请输入手机号'
        isValid = false
      }

      if (!this.addFormData.role) {
        this.addFormErrors.role = '请选择角色'
        isValid = false
      }

      if (this.addFormData.role === 'STORE_ADMIN' && this.addFormData.managedStoreIds.length === 0) {
        this.addFormErrors.managedStoreIds = '请至少选择一个可管理门店'
        isValid = false
      }

      return isValid
    },

    handleRoleChange(formType) {
      const targetForm = formType === 'edit' ? this.editFormData : this.addFormData
      const targetErrors = formType === 'edit' ? this.editFormErrors : this.addFormErrors
      if (targetForm.role !== 'EMPLOYEE') {
        targetForm.employeePermissions = []
        delete targetErrors.employeePermissions
      }
      if (targetForm.role !== 'STORE_ADMIN') {
        targetForm.managedStoreIds = []
        delete targetErrors.managedStoreIds
      } else {
        targetForm.storeId = ''
        delete targetErrors.storeId
      }
    },

    normalizeEmployeePermissions(value) {
      if (Array.isArray(value)) {
        return value.filter(Boolean)
      }
      if (typeof value === 'string' && value.trim()) {
        return value.split(',').map(item => item.trim()).filter(Boolean)
      }
      return []
    },

    normalizeStoreIds(value) {
      if (Array.isArray(value)) {
        return value.map(item => Number(item)).filter(Boolean)
      }
      if (typeof value === 'string' && value.trim()) {
        return value.split(',').map(item => Number(item.trim())).filter(Boolean)
      }
      return []
    },

    async handleAddSubmit() {
      if (!this.validateAddForm()) {
        return
      }

      this.addFormLoading = true
      try {
        const response = await request.post('/api/users/createUser', {
          username: this.addFormData.username.trim(),
          password: this.addFormData.password.trim(),
          phone: this.addFormData.phone.trim(),
          role: this.addFormData.role,
          storeId: this.addFormData.storeId || null,
          email: this.addFormData.email.trim() || null,
          employeePermissions: [],
          managedStoreIds: this.addFormData.role === 'STORE_ADMIN' ? this.addFormData.managedStoreIds : []
        })

        if (response.data.success && response.data.code === 200) {
          this.handleCloseAddModal()
          this.loadUserList()
        } else {
          throw new Error(response.data.message || '新增用户失败')
        }
      } catch (error) {
        console.error('新增用户失败:', error)
        alert(error.message || '新增用户失败，请重试')
      } finally {
        this.addFormLoading = false
      }
    },

    // 编辑用户相关方法
    handleEdit(user) {
      this.showEditModal = true
      this.editFormData = {
        id: user.id,
        username: user.username || '',
        password: '', // 密码字段不预填充，需要重新输入
        phone: user.phone || '',
        role: user.role || 'STORE_ADMIN',
        storeId: user.storeId || '',
        email: user.email || '',
        employeePermissions: this.normalizeEmployeePermissions(user.employeePermissions),
        managedStoreIds: this.normalizeStoreIds(user.managedStoreIds)
      }
      this.editFormErrors = {}
      this.editFormLoading = false
    },

    handleCloseEditModal() {
      this.showEditModal = false
      this.resetEditForm()
    },

    resetEditForm() {
      this.editFormData = {
        id: null,
        username: '',
        password: '',
        phone: '',
        role: 'STORE_ADMIN',
        storeId: '',
        email: '',
        employeePermissions: [],
        managedStoreIds: []
      }
      this.editFormErrors = {}
      this.editFormLoading = false
    },

    validateEditForm() {
      this.editFormErrors = {}
      let isValid = true

      if (!this.editFormData.username.trim()) {
        this.editFormErrors.username = '请输入用户名'
        isValid = false
      }

      if (!this.editFormData.phone.trim()) {
        this.editFormErrors.phone = '请输入手机号'
        isValid = false
      }

      if (!this.editFormData.role) {
        this.editFormErrors.role = '请选择角色'
        isValid = false
      }

      if (this.editFormData.role === 'STORE_ADMIN' && this.editFormData.managedStoreIds.length === 0) {
        this.editFormErrors.managedStoreIds = '请至少选择一个可管理门店'
        isValid = false
      }

      return isValid
    },

    async handleEditSubmit() {
      if (!this.validateEditForm()) {
        return
      }

      this.editFormLoading = true
      try {
        const response = await request.post(`/api/users/updateUser?id=${this.editFormData.id}`, {
          username: this.editFormData.username.trim(),
          password: this.editFormData.password.trim() || null,
          phone: this.editFormData.phone.trim(),
          role: this.editFormData.role,
          storeId: this.editFormData.storeId || null,
          email: this.editFormData.email.trim() || null,
          employeePermissions: [],
          managedStoreIds: this.editFormData.role === 'STORE_ADMIN' ? this.editFormData.managedStoreIds : []
        })

        if (response.data.success && response.data.code === 200) {
          this.handleCloseEditModal()
          this.loadUserList()
        } else {
          throw new Error(response.data.message || '更新用户失败')
        }
      } catch (error) {
        console.error('更新用户失败:', error)
        alert(error.message || '更新用户失败，请重试')
      } finally {
        this.editFormLoading = false
      }
    },

    // 删除用户相关方法
    handleDelete(user) {
      this.showDeleteModal = true
      this.deleteUserData = {
        id: user.id,
        username: user.username
      }
      this.deleteLoading = false
    },

    handleCloseDeleteModal() {
      this.showDeleteModal = false
      this.deleteUserData = {
        id: null,
        username: ''
      }
      this.deleteLoading = false
    },

    async handleDeleteConfirm() {
      this.deleteLoading = true
      try {
        const response = await request.delete(`/api/users/deleteUser?id=${this.deleteUserData.id}`)

        if (response.data.success && response.data.code === 200) {
          this.handleCloseDeleteModal()
          this.loadUserList()
        } else {
          throw new Error(response.data.message || '删除用户失败')
        }
      } catch (error) {
        console.error('删除用户失败:', error)
        alert(error.message || '删除用户失败，请重试')
      } finally {
        this.deleteLoading = false
      }
    }

  }
}
</script>

<style scoped>
.account-management {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索区域样式 */
.search-section {
  margin-bottom: 24px;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-input,
.search-select {
  flex: 1;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-select {
  flex: 0 0 160px;
}

.search-input:focus,
.search-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-btn, .reset-btn, .add-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-btn {
  background-color: #007bff;
  color: white;
}

.search-btn:hover {
  background-color: #0056b3;
}

.permission-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fafafa;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.error-box {
  border-color: #dc3545;
}

.reset-btn {
  background-color: #6c757d;
  color: white;
}

.reset-btn:hover {
  background-color: #545b62;
}

.add-btn {
  background-color: #28a745;
  color: white;
}

.add-btn:hover {
  background-color: #218838;
}

/* 表格区域样式 */
.table-section {
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f8f9fa;
  color: #333;
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.data-row:hover {
  background-color: #f8f9fa;
}

.loading-row, .empty-row {
  text-align: center;
}

.loading-cell, .empty-cell {
  padding: 40px;
  color: #666;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.action-btn {
  padding: 4px 8px;
  margin-right: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.edit-btn {
  color: #1890ff;
  border-color: #1890ff;
}

.edit-btn:hover {
  background: #e6f7ff;
  border-color: #40a9ff;
}

.delete-btn {
  color: #ff4d4f;
  border-color: #ff4d4f;
}

.delete-btn:hover {
  background: #fff2f0;
  border-color: #ff7875;
}

/* 分页样式 */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px 0;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background-color: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
  border-color: #007bff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-numbers {
  display: flex;
  gap: 4px;
  margin: 0 8px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 24px;
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #ff4d4f;
}

.form-input, .form-select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-input.error, .form-select.error {
  border-color: #ff4d4f;
}

.readonly-role {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f7f8fa;
  color: #666;
}

.form-input.error:focus, .form-select.error:focus {
  border-color: #ff4d4f;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #eee;
}

.cancel-btn, .submit-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background-color: #545b62;
}

.submit-btn {
  background-color: #007bff;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* 删除确认弹窗样式 */
.delete-modal {
  max-width: 400px;
}

.warning-text {
  color: #ff4d4f;
  font-size: 14px;
  margin: 8px 0 0 0;
  font-weight: 500;
}

.delete-confirm-btn {
  background-color: #ff4d4f;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.delete-confirm-btn:hover:not(:disabled) {
  background-color: #d32f2f;
}

.delete-confirm-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
</style>
